import { defineStore } from 'pinia'
import type { RouteLocationNormalized } from 'vue-router'
import { buildAccountPostTitle } from '@/views/PublicopinionContent/components/accountHistory'

export interface TabItem {
  title: string
  name: string // unique key, use fullPath
  path: string
  fullPath: string
  closable?: boolean
  componentName?: string // 新增：用于 keep-alive 缓存控制
}

function buildTitle(route: RouteLocationNormalized): string {
  const base = (route.meta?.title as string) || (route.name as string) || '未命名'
  const routeName = (route.name as string) || ''
  // PublicopinionContent 初始不带 id，后续由页面数据更新为标题前五个字符
  if (routeName === 'PublicopinionContent') return base
  if (routeName === 'AccountPostList') {
    const nickname = Array.isArray(route.query.nickname)
      ? route.query.nickname[0]
      : route.query.nickname
    return buildAccountPostTitle(String(route.params.type ?? ''), nickname)
  }
  if (routeName === 'MediaDetails') {
    const mediaName = Array.isArray(route.query.mediaName)
      ? route.query.mediaName[0]
      : route.query.mediaName
    if (mediaName) return `${base}-${mediaName}`
    return base
  }
  const id = (route.params as any)?.id || (route.query as any)?.id
  if (id) return `${base}-${id}`
  return base
}

function getIdFromFullPath(fp: string): string {
  return fp.split('#')[0].split('?')[0].split('/').filter(Boolean).pop() || ''
}

export const useTabsStore = defineStore('tabs', {
  state: () => ({
    tabs: [] as TabItem[],
    active: '' as string,
    // 用于 keep-alive 的组件名称列表
    cachedViews: [] as string[],
  }),
  actions: {
    initWithRoute(route: RouteLocationNormalized) {
      this.addByRoute(route)
      this.active = route.fullPath
    },
    addByRoute(route: RouteLocationNormalized) {
      const name = route.fullPath
      if (!name) return
      const componentName = route.name as string
      const exists = this.tabs.find((t) => t.name === name)
      if (!exists) {
        this.tabs.push({
          title: buildTitle(route),
          name,
          path: route.path,
          fullPath: route.fullPath,
          closable: true,
          componentName: componentName,
        })
      } else {
        // 已存在标签：除舆情内容页外才用路由meta刷新标题，避免被重置为“舆情内容”
        const routeName = (route.name as string) || ''
        if (routeName !== 'PublicopinionContent') {
          exists.title = buildTitle(route)
        }
      }
      this.active = name

      // 将组件名添加到缓存列表（如果 meta 中配置了 noCache 则不添加）
      if (componentName && !route.meta?.noCache) {
        if (!this.cachedViews.includes(componentName)) {
          this.cachedViews.push(componentName)
        }
      }
    },
    remove(name: string) {
      const idx = this.tabs.findIndex((t) => t.name === name)
      if (idx === -1) return undefined as string | undefined

      const removedTab = this.tabs[idx]
      const isActive = this.active === name
      const next = this.tabs[idx + 1] || this.tabs[idx - 1]

      this.tabs.splice(idx, 1)

      // 如果没有其他相同组件名的标签了，从缓存列表中移除组件名
      if (removedTab.componentName) {
        const hasSameComponent = this.tabs.some(t => t.componentName === removedTab.componentName)
        if (!hasSameComponent) {
          const cIdx = this.cachedViews.indexOf(removedTab.componentName)
          if (cIdx > -1) {
            this.cachedViews.splice(cIdx, 1)
          }
        }
      }

      if (isActive) {
        this.active = next?.name || ''
      }
      return this.active
    },
    // 专门用于清理缓存的方法
    removeCachedView(route: RouteLocationNormalized) {
      const componentName = route.name as string
      if (componentName) {
        const index = this.cachedViews.indexOf(componentName)
        if (index > -1) {
          this.cachedViews.splice(index, 1)
        }
      }
    },
    setActive(name: string) {
      this.active = name
    },
    // 仅允许 /public-opinion-content/** 的标签被更新标题（按 fullPath 匹配）
    updateTitle(fullPath: string, title: string) {
      let t = this.tabs.find((x) => x.fullPath === fullPath)
      // 若未找到，尝试通过 id 匹配同为舆情内容页的标签（处理 fullPath 规范化或追加查询参数导致不一致）
      if (!t) {
        const id = getIdFromFullPath(fullPath)
        if (id) {
          t = this.tabs.find(
            (x) => (x.path.startsWith('/public-opinion-content') || x.fullPath.startsWith('/public-opinion-content')) && getIdFromFullPath(x.fullPath) === id
          )
        }
      }
      if (!t) return
      // 白名单：只允许舆情内容页（兼容 path 或 fullPath 判定）
      const isPublicOpinion = t.path.startsWith('/public-opinion-content') || t.fullPath.startsWith('/public-opinion-content')
      if (!isPublicOpinion) return
      t.title = title
    },
    // 新增：按 id 更新舆情内容标签标题，更稳健
    updateTitleById(id: string, title: string) {
      if (!id) return
      const t = this.tabs.find(
        (x) => (x.path.startsWith('/public-opinion-content') || x.fullPath.startsWith('/public-opinion-content')) && getIdFromFullPath(x.fullPath) === id
      )
      if (!t) return
      t.title = title
    },
    updateTitleByFullPath(fullPath: string, title: string) {
      if (!fullPath || !title) return
      const tab = this.tabs.find((item) => item.fullPath === fullPath)
      if (tab) tab.title = title
    },
    reset() {
      this.tabs = []
      this.active = ''
    },
    // 更新标签顺序（用于拖拽排序）
    reorderTabs(newOrder: TabItem[]) {
      this.tabs = newOrder
    },
  },
})
